import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../core/Styles';
import { getPasswordChecks } from '../../../utils/password-policy';

const PRIVACY_URL = 'https://risenhope.com/privacy';
const TERMS_URL = 'https://risenhope.com/terms';

export const AuthSubmitButton = ({
  title,
  onClick,
  loading,
  disabled,
}: {
  title: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}) => {
  const off = disabled || loading;
  return (
    <SubmitBtn type="button" disabled={!!off} onClick={onClick}>
      {loading ? <Spinner /> : title}
    </SubmitBtn>
  );
};

export const SocialButton = ({
  title,
  onPress,
  variant,
  disabled,
}: {
  title: string;
  onPress: () => void;
  variant: 'google' | 'apple';
  disabled?: boolean;
}) => {
  const isApple = variant === 'apple';
  return (
    <SocialBtn type="button" $apple={isApple} disabled={!!disabled} onClick={onPress}>
      {variant === 'google' ? (
        <GoogleIcon>
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        </GoogleIcon>
      ) : (
        <AppleIcon>
          <svg viewBox="0 0 24 24" width="18" height="18" fill={isApple ? '#fff' : '#000'}>
            <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
          </svg>
        </AppleIcon>
      )}
      <SocialBtnText $apple={isApple}>{title}</SocialBtnText>
    </SocialBtn>
  );
};

export const ErrorBanner = ({ message }: { message: string }) => <ErrorDiv>{message}</ErrorDiv>;

export const Divider = ({ label }: { label: string }) => (
  <DividerRow>
    <DividerLine />
    <DividerLabel>{label}</DividerLabel>
    <DividerLine />
  </DividerRow>
);

export const TermsNotice = ({ prefix = 'By continuing' }: { prefix?: string }) => (
  <TermsText>
    {prefix}, you agree to the{' '}
    <TermsLink href={TERMS_URL} target="_blank" rel="noopener noreferrer">
      Terms of Use
    </TermsLink>{' '}
    and{' '}
    <TermsLink href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
      Privacy Policy
    </TermsLink>
    .
  </TermsText>
);

const PW_ITEMS: { key: 'length' | 'lowercase' | 'uppercase' | 'number'; label: string }[] = [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'lowercase', label: '1 lowercase letter' },
  { key: 'uppercase', label: '1 uppercase letter' },
  { key: 'number', label: '1 number' },
];

export const PasswordChecklist = ({ password }: { password: string }) => {
  const checks = getPasswordChecks(password);
  return (
    <ChecklistWrap>
      {PW_ITEMS.map(item => {
        const met = checks[item.key];
        return (
          <CheckItem key={item.key} $met={met}>
            <CheckMark>{met ? '✓' : '•'}</CheckMark>
            {item.label}
          </CheckItem>
        );
      })}
    </ChecklistWrap>
  );
};

export const AuthInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  error,
  autoFocus,
  onKeyDown,
  onFocus,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  error?: string | null;
  autoFocus?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}) => (
  <InputWrap>
    <InputLabel>{label}</InputLabel>
    <StyledInput
      type={type}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      $hasError={!!error}
      onChange={e => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    />
    {error && <InputError>{error}</InputError>}
  </InputWrap>
);

export const AuthCard = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  padding: 24px;
`;

export const AuthInner = styled.div`
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
`;

export const AuthLogo = styled.img`
  height: 64px;
  width: 180px;
  object-fit: contain;
  align-self: center;
  margin-top: 16px;
  margin-bottom: 40px;
`;

export const AuthTitle = styled.h2`
  font-family: Nunito, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px;
`;

export const AuthFooterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
  font-family: Nunito, sans-serif;
  font-size: 14px;
  color: #8a9099;
`;

export const AuthLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Nunito, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${colors.primary};
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const SubmitBtn = styled.button`
  && {
    width: 100%;
    padding: 14px;
    border-radius: 8px;
    border: none;
    background: linear-gradient(to right, #102a65, #0038b8);
    color: #fff;
    font-family: Nunito, sans-serif;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
    &:disabled {
      background: #9fb0d8;
      cursor: not-allowed;
    }
  }
`;

const SocialBtn = styled.button<{ $apple: boolean }>`
  && {
    width: 100%;
    padding: 14px;
    border-radius: 8px;
    border: 1px solid ${p => (p.$apple ? '#000' : '#dde1e5')};
    background: ${p => (p.$apple ? '#000' : '#fff')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    min-height: 52px;
    text-transform: none;
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const SocialBtnText = styled.span<{ $apple: boolean }>`
  font-family: Nunito, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${p => (p.$apple ? '#fff' : '#333')};
`;

const GoogleIcon = styled.span`
  display: flex;
  align-items: center;
`;
const AppleIcon = styled.span`
  display: flex;
  align-items: center;
`;

const ErrorDiv = styled.div`
  background: #fdecec;
  border: 1px solid #f5c2c2;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: Nunito, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #b03030;
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #dde1e5;
`;

const DividerLabel = styled.span`
  font-family: Nunito, sans-serif;
  font-size: 13px;
  color: #8a9099;
`;

const TermsText = styled.p`
  font-family: Nunito, sans-serif;
  font-size: 12px;
  color: #8a9099;
  text-align: center;
  line-height: 18px;
  margin: 0;
`;

const TermsLink = styled.a`
  font-weight: 700;
  color: ${colors.primary};
  text-decoration: none;
`;

const ChecklistWrap = styled.div`
  margin-top: 6px;
`;

const CheckItem = styled.div<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Nunito, sans-serif;
  font-size: 12px;
  color: ${p => (p.$met ? '#2e9e5b' : '#8a9099')};
  margin-bottom: 2px;
`;

const CheckMark = styled.span`
  width: 14px;
`;

const InputWrap = styled.div`
  margin-bottom: 0;
`;

const InputLabel = styled.label`
  display: block;
  font-family: Nunito, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
`;

const StyledInput = styled.input<{ $hasError: boolean }>`
  width: 100%;
  padding: 11px 14px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$hasError ? '#f5c2c2' : '#dde1e5')};
  font-family: Nunito, sans-serif;
  font-size: 14px;
  color: #333;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: ${colors.primary};
  }
  &::placeholder {
    color: #b0b7bf;
  }
`;

const InputError = styled.span`
  font-family: Nunito, sans-serif;
  font-size: 12px;
  color: #b03030;
  margin-top: 4px;
  display: block;
`;
